/***************************************************************************************
 * FileName      : PlaybackChart.tsx
 * Description   : 시뮬레이션 진행에 따라 센서/제어 값 변화를 누적 시계열로 표시.
 *                 범례를 chip 버튼으로 커스텀 렌더 — 클릭으로 개별 시리즈 토글(다중 선택).
 *                 boolean 제어값은 0/1로 stepAfter 라인 렌더링.
 * Author        : (주)한국공학기술연구원 박은정
 * Created Date  : 2026.05.13
 * Modified Date : 2026.05.13 좌측 패널 폭에 맞게 전체 축소, 범례를 버튼(chip) 모양으로 변경
 * Reference     : recharts v2 LineChart + custom Legend content
 ***************************************************************************************/

import React, { useMemo, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

export interface ChartPoint {
  time: number;
  [seriesKey: string]: number;
}

interface Props {
  history: ChartPoint[];
}

// 시리즈 색상 팔레트 (10개: 센서 warm 5 + 제어 cool 3 + bool accent 2)
const PALETTE = [
  '#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16',
  '#06b6d4', '#3b82f6', '#8b5cf6',
  '#10b981', '#ec4899',
];

// 시리즈가 boolean 출처(0/1만)인지 판정 → step 라인으로 표현
const isBoolSeries = (history: ChartPoint[], key: string): boolean => {
  if (history.length === 0) return false;
  for (const p of history) {
    const v = p[key];
    if (v !== 0 && v !== 1) return false;
  }
  return true;
};

export const PlaybackChart: React.FC<Props> = ({ history }) => {
  // 데이터 첫 포인트에서 시리즈 키 자동 추출 (time 제외)
  const seriesKeys = useMemo(() => {
    if (history.length === 0) return [];
    return Object.keys(history[0]).filter(k => k !== 'time');
  }, [history]);

  // 숨김 처리된 시리즈 집합 (기본: 전부 표시)
  const [hidden, setHidden] = useState<Set<string>>(new Set());

  const toggleSeries = (key: string) => {
    setHidden(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (history.length === 0) return null;

  return (
    <div className="playback-chart">
      <div className="playback-chart__header">
        <h4 className="playback-chart__title">값 변화 추이</h4>
        <span className="playback-chart__hint">버튼 클릭으로 토글</span>
      </div>
      <ResponsiveContainer width="100%" height={180}>
        <LineChart data={history} margin={{ top: 6, right: 10, bottom: 4, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="time"
            type="number"
            domain={['dataMin', 'dataMax']}
            tickFormatter={(v: number) => `${v}s`}
            stroke="#9ca3af"
            tick={{ fontSize: 9 }}
            tickMargin={2}
          />
          <YAxis
            stroke="#9ca3af"
            tick={{ fontSize: 9 }}
            width={28}
            tickMargin={2}
          />
          <Tooltip
            labelFormatter={(label: number) => `t=${label}s`}
            contentStyle={{ fontSize: 10, padding: '4px 6px' }}
            itemStyle={{ padding: 0 }}
          />
          {seriesKeys.map((key, idx) => {
            const stepLine = isBoolSeries(history, key);
            const color = PALETTE[idx % PALETTE.length];
            const isHidden = hidden.has(key);
            return (
              <Line
                key={key}
                type={stepLine ? 'stepAfter' : 'monotone'}
                dataKey={key}
                stroke={color}
                strokeWidth={1.5}
                dot={false}
                hide={isHidden}
                isAnimationActive={false}
              />
            );
          })}
        </LineChart>
      </ResponsiveContainer>
      {/* 2026.05.13 : 박은정 : 범례를 recharts 기본 대신 chip 버튼으로 렌더 (다중 토글, 좌측 패널 폭 절약) */}
      <div className="playback-chart__legend">
        {seriesKeys.map((key, idx) => {
          const color = PALETTE[idx % PALETTE.length];
          const isHidden = hidden.has(key);
          return (
            <button
              key={key}
              type="button"
              className={`playback-chart__chip${isHidden ? ' playback-chart__chip--off' : ''}`}
              onClick={() => toggleSeries(key)}
              style={{
                borderColor: color,
                backgroundColor: isHidden ? '#ffffff' : color,
                color: isHidden ? color : '#ffffff',
              }}
              title={isHidden ? `${key} 표시` : `${key} 숨김`}
            >
              {key}
            </button>
          );
        })}
      </div>
    </div>
  );
};
